import { useEffect, useState } from "react";
import { Button, Card, Form } from "react-bootstrap";

import { MapSettingsColor } from "../../../services/models/map_settings";
import { MapSettingsColorPicker } from "./MapSettingsColorPicker";
import styles from "./MapSettingsColorBlock.module.css";
import sButtons from "../../../assets/styles/buttons.module.css";

interface Props {
  colors: MapSettingsColor[];
  onSave: (colors: MapSettingsColor[]) => void;
  onColorPickerVisibilityChange?: (isVisible: boolean) => void;
}

interface ChangeColorState {
  color: string;
  index: number;
}

interface ErrorState {
  [index: number]: {
    minValue?: boolean;
    maxValue?: boolean;
  };
}

export const MapSettingsColorBlock = ({ colors, onSave, onColorPickerVisibilityChange }: Props) => {
  const [ranges, setRanges] = useState<MapSettingsColor[]>([]);
  const [changeColor, setChangeColor] = useState<ChangeColorState | null>(null);
  const [errors, setErrors] = useState<ErrorState>({});
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  useEffect(() => {
    setRanges(colors);
  }, [colors]);

  const validateRange = (index: number) => {
    const newErrors = { ...errors };
    const currentRange = ranges[index];

    // Проверка minValue
    if (currentRange.minValue > currentRange.maxValue) {
      newErrors[index] = { ...newErrors[index], minValue: true };
    } else if (index > 0 && currentRange.minValue < ranges[index - 1].maxValue) {
      newErrors[index] = { ...newErrors[index], minValue: true };
    } else {
      if (newErrors[index]) delete newErrors[index].minValue;
    }

    // Проверка maxValue
    if (currentRange.maxValue < currentRange.minValue) {
      newErrors[index] = { ...newErrors[index], maxValue: true };
    } else if (index < ranges.length - 1 && currentRange.maxValue > ranges[index + 1].minValue) {
      newErrors[index] = { ...newErrors[index], maxValue: true };
    } else {
      if (newErrors[index]) {
        delete newErrors[index].maxValue;
      }
    }

    if (!Object.keys(newErrors[index] || {}).length) {
      delete newErrors[index];
    }

    setErrors(newErrors);
  };

  const updateMovedRangeMaxValue = (ranges: MapSettingsColor[], movedRangeIndex: number): MapSettingsColor[] => {
    const updatedRanges = [...ranges];
    // Если есть следующий диапазон, обновляем maxValue перемещенного диапазона
    if (movedRangeIndex < updatedRanges.length - 1) {
      updatedRanges[movedRangeIndex] = {
        ...updatedRanges[movedRangeIndex],
        maxValue: updatedRanges[movedRangeIndex + 1].minValue
      };
    }
    return updatedRanges;
  };

  const handleChangeRange = (index: number, field: keyof Omit<MapSettingsColor, "color">, value: string) => {
    const newRanges = [...ranges];
    const changedRange = {
      ...newRanges[index],
      [field]: value === "" ? "" : Number(value),
    };
    newRanges[index] = changedRange;

    // Сортируем только если изменилось минимальное значение
    if (field === "minValue") {
      const sortedRanges = newRanges.sort((a, b) => a.minValue - b.minValue);
      
      // Находим новый индекс измененного диапазона
      const newIndex = sortedRanges.findIndex(range => 
        range.minValue === changedRange.minValue && 
        range.maxValue === changedRange.maxValue && 
        range.color === changedRange.color
      );

      // Обновляем maxValue у перемещенного диапазона
      const updatedRanges = updateMovedRangeMaxValue(sortedRanges, newIndex);
      
      // Сохраняем новый индекс для фокуса
      setFocusedIndex(newIndex);
      setRanges(updatedRanges);
    } else {
      // Если изменяется maxValue, просто обновляем массив без сортировки
      setRanges(newRanges);
    }
  };

  useEffect(() => {
    // Устанавливаем фокус на нужный элемент после обновления
    if (focusedIndex !== null) {
      const input = document.querySelector(`[data-range-index="${focusedIndex}"] input`);
      if (input instanceof HTMLInputElement) {
        input.focus();
      }
      setFocusedIndex(null);
    }
  }, [focusedIndex, ranges]);

  const handleAddRange = () => {
    const lastRange = ranges[ranges.length - 1];
    const newRange: MapSettingsColor = {
      minValue: lastRange ? lastRange.maxValue : 0,
      maxValue: lastRange ? lastRange.maxValue + 1 : 1,
      color: "#ffffff",
    };

    // Добавляем новый диапазон и сортируем по minValue
    const newRanges = [...ranges, newRange].sort((a, b) => a.minValue - b.minValue);
    setRanges(newRanges);
  };

  const handleRemoveRange = (index: number) => {
    const newRanges = ranges.filter((_, i) => i !== index);
    const newErrors = { ...errors };

    delete newErrors[index];

    const updatedErrors: ErrorState = {};
    Object.entries(newErrors).forEach(([key, value]) => {
      const newIndex = parseInt(key, 10);
      if (newIndex > index) {
        updatedErrors[newIndex - 1] = value;
      } else {
        updatedErrors[newIndex] = value;
      }
    });

    setRanges(newRanges);
    setErrors(updatedErrors);
  };

  const handleOpenColorPicker = (color: string, index: number) => {
    setChangeColor({ color, index });
    onColorPickerVisibilityChange?.(true);
  };

  const handleCloseColorPicker = () => {
    setChangeColor(null);
    onColorPickerVisibilityChange?.(false);
  };

  const handleChangeColor = (color: string) => {
    if (changeColor) {
      const newRanges = [...ranges];
      newRanges[changeColor.index] = {
        ...newRanges[changeColor.index],
        color,
      };
      setRanges(newRanges);
      handleCloseColorPicker();
    }
  };

  return (
    <div className="d-flex flex-column gap-3">
      <div className="d-flex flex-column gap-2">
        {ranges.map((range, index) => (
          <Card key={range+"_"+index} className={styles.cardWrapper}>
            <Card.Body className={styles.cardBody}>
              <div className="d-flex gap-3 align-items-center justify-content-end">
                <div className={styles.colorBlock} style={{ backgroundColor: range.color }}></div>
                <div className={styles.formGroupsWrapper} data-range-index={index}>
                  <Form.Group>
                    <Form.Control
                      type="number"
                      value={range.minValue}
                      step={0.1}
                      onChange={(e) => handleChangeRange(index, "minValue", e.target.value)}
                      onBlur={() => validateRange(index)}
                      className={`${styles.formControl} ${styles.noSpinners} ${errors[index]?.minValue ? styles.error : ""}`}
                    />
                  </Form.Group>
                  <span>—</span>
                  <Form.Group>
                    <Form.Control
                      type="number"
                      value={range.maxValue}
                      step={0.1}
                      onChange={(e) => handleChangeRange(index, "maxValue", e.target.value)}
                      onBlur={() => validateRange(index)}
                      className={`${styles.formControl} ${styles.noSpinners} ${errors[index]?.maxValue ? styles.error : ""}`}
                    />
                  </Form.Group>
                </div>
                <div className="d-flex flex-row gap-3">
                  <button
                    onClick={() => handleOpenColorPicker(range.color, index)}
                    className={`ms-auto w-100 ${styles.buttonColor}`}
                    title={"Изменить цвет"}
                  >
                  </button>
                  <button
                      onClick={() => handleRemoveRange(index)}
                      className={`ms-auto w-100 ${styles.buttonDelete}`}
                      title={"Удалить"}
                  >
                  </button>
                </div>
              </div>
            </Card.Body>
          </Card>
        ))}
      </div>

      <button className={`${sButtons.iconButton} ${sButtons.addButton} ${styles.addRangeButton}`} onClick={handleAddRange}>Добавить диапазон</button>

      <div className="d-flex gap-3">
        <button onClick={() => setRanges(colors)} className={sButtons.dangerButton} >
          Сбросить изменения
        </button>
        <button
          onClick={() => onSave(ranges)}
          className={sButtons.successButton}
          disabled={Object.keys(errors).length > 0}
        >
          Сохранить
        </button>
      </div>

      <MapSettingsColorPicker
        isShowModal={!!changeColor}
        currentColor={changeColor?.color}
        onSave={handleChangeColor}
        onClose={handleCloseColorPicker}
      />
    </div>
  );
};
